import axios from "axios";
import {PDFDocument, StandardFonts} from 'pdf-lib';
import helperMixin from "@utilities/helperMixin";
import {formatDateString} from "@utilities/helpers.js";

export default {
    mixins: [helperMixin],
    data() {
        return {
            baseURL: localized.baseURL,
        }
    },
    computed: {
        user() {
            return this.$store.state.user;
        },
        isAdmin() {
            const admin = this.user?.roles?.includes("administrator") || false;
            const editor = this.user?.roles?.includes("editor") || false;
            return admin || editor;
        },
        isManager() {
            return this.user?.roles?.includes("sb_manager_role") || false;
        },
        isSubscriber() {
            return this.user?.roles?.includes("subscriber") || false;
        },
        isContributor() {
            return this.user?.roles?.includes("contributor") || false;
        },
    },
    methods: {
        getCustomers() {
            axios.get(this.baseURL + 'customers')
                .then(response => {
                    this.$store.commit('setCustomers', response.data);
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getTemplates() {
            axios.get(this.baseURL + 'templates')
                .then(response => {
                    this.$store.commit('setTemplates', response.data);
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getForms() {
            axios.get(this.baseURL + 'forms')
                .then(response => {
                    this.$store.commit('setForms', response.data);
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getFormData(formId) {
            const formParam = formId ?? 0;
            axios.get(this.baseURL + 'forms/questions/' + formParam)
                .then(response => {
                    const formQNums = {};
                    for (let field of response.data) {
                        formQNums[field['property']] = field['q_num'];
                    }
                    this.$store.commit('setFormQNums', formQNums);
                })
                .catch(error => {
                    console.error(error);
                });
        },
        postFormData(formId, data) {
            console.log('Request: ', this.baseURL);
            axios.post(this.baseURL + 'forms/questions/' + formId, data)
                .then(response => {
                    this.$store.commit('setFormQNums', response.data);
                    this.getFormData(formId);
                })
                .catch(error => {
                    console.error(error);
                });
        },
        generateAllDocs(customerId, templateIds, techId, shopId) {
            for (let templateId of templateIds) {
                this.createClickGenerateLink(customerId, templateId, techId, shopId);
            }
        },
        createClickGenerateLink(customerId, templateId, tech, shop) {
            axios.post('/wp-admin/admin-ajax.php', {
                action: 'generate_pdf',
                customerID: customerId,
                templateID: templateId
            }, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                responseType: 'blob',
            })
                .then(async response => {
                    let blob;
                    if (parseInt(templateId) === 19) {
                        blob = await this.modifyContractPDF(response.data, tech, shop);
                    } else {
                        blob = new Blob([response.data], {type: 'application/pdf'});
                    }
                    let url = window.URL.createObjectURL(blob);
                    window.open(url, '_blank');
                })
                .catch(error => {
                    console.error(error);
                });
        },
        async modifyContractPDF(pdfData, tech, shop) {
            const arrayBuffer = await pdfData.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const pages = pdfDoc.getPages();
            const firstPage = pages[0];
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
            const size = 10;
            const maxWidth = 200;
            const lineHeight = 15;

            const addressParts = shop.address.split(', ');

            firstPage.drawText(tech.full_name ?? '', {
                x: 160, y: 699,
                size, font,
            });
            firstPage.drawText(tech.doc_prep ?? '', {
                x: 125, y: 630,
                size, font,
            });
            firstPage.drawText(addressParts[0] ?? '', {
                x: 160, y: 681,
                size, font
            });
            firstPage.drawText(`${addressParts[1] ?? ''}, ${addressParts[2] ?? ''}`, {
                x: 160, y: 670,
                size, font
            });
            firstPage.drawText(tech.bond ?? '', {
                x: 370, y: 676,
                size, font,
            });
            firstPage.drawText(shop.phone ?? '', {
                x: 153, y: 652,
                size, font,
            });
            firstPage.drawText(shop.biz_lic ?? '', {
                x: 444, y: 652,
                size, font,
            });
            firstPage.drawText(shop.biz_lic_expiry ?? '', {
                x: 455, y: 630,
                size, font,
            });

            const modifiedPdfBytes = await pdfDoc.save();

            return new Blob([modifiedPdfBytes], {type: 'application/pdf'});
        },
        postTransaction(customer, formQNums, technicianIds) {
            axios.post(this.baseURL + 'transaction', {
                customer: customer,
                formQNums: formQNums,
                techs: technicianIds,
                shopId: sessionStorage.getItem('sbShopId'),
            })
                .then(response => {
                    console.log("service successfully stored");
                })
                .catch(error => {
                    console.error(error);
                });
        },
        updateTransaction(transaction) {
            // transaction.created_at still goes since it is allowed to be updated
            delete transaction.updated_at // updated at is handled automatically by Eloquent on the back end
            axios.put(this.baseURL + 'transaction', transaction)
                .then(response => {
                    console.log("service successfully updated");
                })
                .catch(error => {
                    console.error(error);
                });
        },
        trashTransaction(transactionId, start, end, shop_id) {
            axios.put(this.baseURL + 'transaction/trash', {
                "id": transactionId
            })
                .then(response => {
                    console.log("service successfully deleted");
                    if (start) {
                        this.getTransactions(start, end, shop_id)
                    }
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getTransactions(start_date, end_date, shop_id, tech_id) {

            let start = start_date ?? new Date(); // Default to today
            start.setHours(0, 0, 0, 0); // 00:00:00.000 today
            start.setMinutes(start.getMinutes() + start.getTimezoneOffset()); // Adjust for DB (UTC) discrepancy
            start = this.formatDatForQuery(start);  // YYYY-MM-DD HH:MM:SS format

            let end = end_date ?? new Date(start);
            end.setHours(24, 0, 0, 0); // 00:00:00.000 tomorrow (24:00:00.000 today)
            end.setMinutes(end.getMinutes() + end.getTimezoneOffset());
            end = this.formatDatForQuery(end);  // YYYY-MM-DD HH:MM:SS format

            axios.get(this.baseURL + 'transaction', {
                params: {start, end, shop_id, tech_id}
            }).then(response => {
                response.data = response.data.map(txn => {
                    txn.techs = txn.techs.map(t => t.id);
                    txn.created_at = new Date(txn.created_at);
                    txn.due_date = formatDateString(txn.due_date)
                    return txn;
                });
                this.$store.commit('setTransactions', response.data);
            })
                .catch(error => {
                    console.error(error);
                });
        },
        shortenDatetimeToDate(dateString) {
            const date = new Date(dateString);
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();
            return `${month}-${day}-${year}`;
        },
        getCurrentUser() {
            axios.get(this.baseURL + 'current_user', {
                headers: {
                    'X-WP-Nonce': localized.nonce
                }
            }).then(response => {
                this.$store.commit('setUser', response.data);
            })
                .catch(error => {
                    console.error(error);
                });
        },
        getAllUsers() {
            axios.get(this.baseURL + 'users')
                .then(response => {
                    this.$store.commit('setUsers', response.data);
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getTechs() {
            axios.get(this.baseURL + 'techs')
                .then(response => {
                    const users = response.data.filter(u => u.user !== null);
                    users.forEach(i => {
                        i.name = i.user.display_name ?? "Placeholder Name";
                        i.user_id = i.user.ID;
                        const first_name = i.user.meta.find(m => m.meta_key === 'first_name').meta_value;
                        const last_name = i.user.meta.find(m => m.meta_key === 'last_name').meta_value;
                        i.first_name = first_name;
                        i.last_name = last_name;
                        i.full_name = first_name + ' ' + last_name;
                    });
                    this.$store.commit('setTechnicians', users);
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getUsers(highestLevel) {
            axios.get(this.baseURL + 'users', {
                params: {highestLevel}
            })
                .then(res => {
                    this.$store.commit('setUsers', Object.values(res.data));
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getShops() {
            axios.get(this.baseURL + 'shops')
                .then(response => {
                    this.$store.commit('setShops', response.data);
                })
                .catch(error => {
                    console.error(error);
                });
        },
        addStore(shop_name) {
            axios.post(this.baseURL + 'shop', {shop_name})
                .then(response => {
                    this.getShops();
                })
                .catch(error => {
                    console.error(error);
                });
        },
        login(user_id, shop_id) {
            sessionStorage.setItem('sbUserId', user_id);
            sessionStorage.setItem('sbShopId', shop_id);
        }
    },
};